import * as yup from 'yup'
import { APIBusinessType } from 'common/types/enums'

// Reusable field schemas
export const emailFieldSchema = yup
  .string()
  .email('Email must be valid')
  .required('Email address is required')

export const passwordFieldSchema = yup.string().required('Password is required')

export const confirmPasswordFieldSchema = yup
  .string()
  .when('password', {
    is: (val: any) => (val && val.length > 0 ? true : false),
    then: (schema) =>
      schema.oneOf([yup.ref('password')], 'Passwords do not match'),
  })
  .required('Required!')

// Auth form schemas
export const loginSchema = yup.object().shape({
  email: emailFieldSchema,
  password: passwordFieldSchema,
})

export const forgotPasswordSchema = yup.object().shape({
  email: emailFieldSchema,
})

export const passwordResetSchema = yup.object().shape({
  confirm_password: confirmPasswordFieldSchema,
  password: passwordFieldSchema,
})

export const addressSchema = yup.object().shape({
  address_line_1: yup.string().required('Required'),
  address_line_2: yup.string().nullable(),
  address_line_3: yup.string().nullable(),
  city: yup.string().required('Required'),
  country: yup.string().required('Required'),
  postal_code: yup.string().required('Required'),
})

export const shippingAddressSchema = yup.object().shape({
  shipping_address_city: yup.string().required('Required'),
  shipping_address_country: yup.string().required('Required'),
  shipping_address_line_1: yup.string().required('Required'),
  shipping_address_line_2: yup.string().nullable(),
  shipping_address_line_3: yup.string().nullable(),
  shipping_address_postcode: yup.string().required('Required'),
})

export const customerSchema = yup.object().shape({
  email_address: yup
    .string()
    .email('Please provide a valid email address')
    .required('Required'),
  first_name: yup.string().required('Required'),
  last_name: yup.string().required('Required'),
})

export const companyInfoSchema = yup.object().shape({
  abn_number: yup
    .string()
    .nullable()
    .test('abn-length', 'ABN must be 11 digits long', (value) => {
      if (!value) return true
      return value.length === 11
    }),
  acn_number: yup
    .string()
    .nullable()
    .test('acn-length', 'ACN must be 9 digits long', (value) => {
      if (!value) return true
      return value.length === 9
    }),
  limited_company_number: yup.string().nullable(),
  name: yup
    .string()
    .nullable()
    .when('organisation_type', {
      is: (v: APIBusinessType) => v !== APIBusinessType.SOLE_TRADER,
      then: (schema) => schema.required('Required'),
    }),
  organisation_type: yup.number().nullable(),
  telephone_number: yup.string().nullable(),
  trading_name: yup
    .string()
    .nullable()
    .when('organisation_type', {
      is: (v: APIBusinessType) => v === APIBusinessType.SOLE_TRADER,
      then: (schema) => schema.required('Required'),
    }),
})

export const memberSchema = yup.object().shape({
  audit_report: yup.mixed().nullable(),
  date_of_birth: yup.string().required('Date of birth is required'),
  first_name: yup.string().required('First name is required'),
  kyc: yup
    .mixed()
    .test(
      'kyc-validation',
      'Either both KYC ID and KYC proof of address must be provided or neither.',
      function () {
        const { kyc_id, kyc_poa } = this.parent
        // If one is provided without the other, return false
        if ((kyc_poa && !kyc_id) || (kyc_id && !kyc_poa)) {
          return this.createError({
            message:
              'Both KYC ID and KYC proof of address must be provided together.',
          })
        }
        // If both are provided or neither, validation passes
        return true
      },
    ),
  kyc_id: yup.mixed().nullable(),
  kyc_poa: yup.mixed().nullable(),
  last_name: yup.string().required('Required'),
  phone_number: yup.string().nullable(),
})

export const companySchema = yup.object().shape({
  company: companyInfoSchema,
  customer: customerSchema,
  external_id: yup.string(),
  groups: yup.array().nullable(),
  members: yup.array().of(memberSchema),
  membersVeriff: yup.array().nullable(),
  product_id: yup.number().nullable(),
  shipping_address: addressSchema,
})
